<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { t } from '@/i18n'
import type { TimeRange, ExportFormat, EditMode } from '@/types'

const props = defineProps<{
  isPlaying: boolean
  loop: boolean
  currentTime: number
  selection: TimeRange
  duration: number
  isExporting: boolean
  exportProgress: number
  editMode: EditMode
}>()

const emit = defineEmits<{
  (e: 'toggle-play'): void
  (e: 'replay'): void
  (e: 'toggle-loop'): void
  (e: 'toggle-mode'): void
  (e: 'seek', time: number): void
  (e: 'export', format: ExportFormat): void
  (e: 'reset'): void
}>()

// Download popup state
const showDownloadPopup = ref(false)
const downloadWrapperRef = ref<HTMLElement | null>(null)

// Computed
const selectionDuration = computed(() => props.selection.end - props.selection.start)
// Length of what would be exported: the selection (cut) or everything else (trim)
const exportDuration = computed(() =>
  props.editMode === 'cut' ? selectionDuration.value : props.duration - selectionDuration.value
)
const canExport = computed(() => exportDuration.value > 0.05 && !props.isExporting)

// Rounded export progress for consistent display
const roundedProgress = computed(() => Math.round(props.exportProgress))

// Mode tooltip
const modeTooltip = computed(() => props.editMode === 'cut' ? t.value.modeCutTooltip : t.value.modeTrimTooltip)

// Handlers
const toggleDownloadPopup = () => {
  if (!canExport.value) return
  showDownloadPopup.value = !showDownloadPopup.value
}

const handleDownload = (format: ExportFormat) => {
  emit('export', format)
  // Don't close popup here - it will close automatically when isExporting becomes true
  // This ensures the progress indicator shows immediately
}

// Close popup when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (downloadWrapperRef.value && !downloadWrapperRef.value.contains(event.target as Node)) {
    showDownloadPopup.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="playback-controls">
    <div class="controls-row">
      <!-- Playback Controls (Center) -->
      <div class="controls-center">
        <!-- Play/Pause -->
        <button
          class="btn btn-primary btn-icon"
          @click="emit('toggle-play')"
          :title="isPlaying ? t.pause : t.play"
          :aria-label="isPlaying ? t.pause : t.play"
        >
          <svg v-if="!isPlaying" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16"/>
            <rect x="14" y="4" width="4" height="16"/>
          </svg>
        </button>

        <!-- Replay -->
        <button class="btn btn-primary btn-icon" @click="emit('replay')" :title="t.replay" :aria-label="t.replay">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
        </button>

        <!-- Loop toggle -->
        <button
          class="btn btn-icon"
          :class="loop ? 'btn-primary' : 'btn-secondary'"
          @click="emit('toggle-loop')"
          :title="t.loop"
          :aria-label="t.loop"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m17 2 4 4-4 4"/>
            <path d="M3 11v-1a4 4 0 0 1 4-4h14"/>
            <path d="m7 22-4-4 4-4"/>
            <path d="M21 13v1a4 4 0 0 1-4 4H3"/>
          </svg>
        </button>

        <!-- Mode toggle (Cut/Trim) -->
        <button
          class="btn btn-icon"
          :class="props.editMode === 'cut' ? 'btn-primary' : 'btn-warning'"
          @click="emit('toggle-mode')"
          :title="modeTooltip"
          :aria-label="modeTooltip"
        >
          <!-- Cut mode icon (scissors) -->
          <svg v-if="props.editMode === 'cut'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="6" cy="6" r="3"/>
            <circle cx="6" cy="18" r="3"/>
            <line x1="20" y1="4" x2="8.12" y2="15.88"/>
            <line x1="14.47" y1="14.48" x2="20" y2="20"/>
            <line x1="8.12" y1="8.12" x2="12" y2="12"/>
          </svg>
          <!-- Trim mode icon (trash/remove) -->
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18"/>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
        </button>
      </div>

      <!-- Playback Actions (New File & Download) -->
      <div class="controls-actions">
        <!-- New File button -->
        <div class="controls-left">
          <button class="btn btn-primary btn-icon" @click="emit('reset')" :title="t.reset" :aria-label="t.reset">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11 6.64a1 1 0 0 0-1.243-.97l-1 .25A1 1 0 0 0 8 6.89v4.306A2.6 2.6 0 0 0 7 11c-.5 0-.974.134-1.338.377-.36.24-.662.628-.662 1.123s.301.883.662 1.123c.364.243.839.377 1.338.377s.974-.134 1.338-.377c.36-.24.662-.628.662-1.123V8.89l2-.5z"/>
              <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
            </svg>
          </button>
        </div>

        <!-- Download button -->
        <div class="controls-right">
          <div class="download-wrapper" ref="downloadWrapperRef">
            <button
              class="btn btn-primary btn-icon"
              @click="toggleDownloadPopup"
              :disabled="!canExport"
              :title="t.download"
              :aria-label="t.download"
            >
              <svg v-if="!isExporting" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" x2="12" y1="15" y2="3"/>
              </svg>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </button>

          <!-- Popup menu -->
          <div v-if="showDownloadPopup && !isExporting" class="download-popup">
            <button class="popup-option" @click="handleDownload('mp3')" :aria-label="t.download + ' MP3'">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              MP3
            </button>
            <button class="popup-option" @click="handleDownload('wav')" :aria-label="t.download + ' WAV'">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              WAV
            </button>
          </div>

            <!-- Export progress -->
            <div v-if="isExporting" class="export-progress" role="progressbar" :aria-valuenow="roundedProgress" aria-valuemin="0" aria-valuemax="100" :aria-label="t.processing">
              <div class="progress-info">
                <span class="progress-label">{{ t.processing }}</span>
                <span class="progress-percent">{{ roundedProgress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.playback-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.controls-left {
  display: flex;
  align-items: center;
}

.controls-center {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  /* Ensure it stays centered on desktop */
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.controls-actions {
  display: contents;
}

.controls-left,
.controls-right {
  display: flex;
  align-items: center;
}

.play-btn {
  padding: 0.75rem 1.5rem;
  gap: 0.5rem;
}

.play-btn svg {
  width: 24px;
  height: 24px;
}

.selection-info {
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.download-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.download-popup {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-popup);
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  z-index: var(--z-popup);
  min-width: 120px;
}

.popup-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
  white-space: nowrap;
  direction: ltr; /* File formats (MP3, WAV) are universal - keep icon on left */
}

.popup-option:hover {
  background: var(--primary-color);
  color: white;
}

.popup-option svg {
  flex-shrink: 0;
}

.export-progress {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  box-shadow: var(--shadow-popup);
  z-index: var(--z-popup);
  min-width: 160px;
  animation: fadeIn var(--transition-normal);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.progress-label {
  font-size: 0.875rem;
  color: var(--text-primary);
  font-weight: 600;
}

.progress-percent {
  font-size: 0.875rem;
  color: var(--success-color);
  font-weight: 700;
  min-width: 40px;
  text-align: start;
}

/* Warning button style for trim mode */
.btn-warning {
  background: var(--warning-color, #f59e0b);
  color: white;
}

.btn-warning:hover {
  filter: brightness(0.9);
}

@media (max-width: 640px) {
  .controls-row {
    gap: 0.5rem;
    flex-direction: column;
    align-items: stretch;
  }

  .controls-left,
  .controls-center,
  .controls-right {
    justify-content: center;
  }

  .controls-center {
    order: -1;
    position: static;
    transform: none;
    margin-bottom: 0.5rem;
  }

  .controls-actions {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
  }

  .download-popup,
  .export-progress {
    left: auto;
    right: 0;
    transform: none;
  }
}
</style>
